// locations.ts — location / encounter defaults + map-marker editing, carved out of template.js's DOM
// UTILITIES junk-drawer (Phase 2g of plans/template-js-carve-up.md) and TS-ported for Phase 2a of
// plans/bundler-script-load-order-phase2.md — the fifth Wave-2a fold into post-template-tail.
//
// Powers the location & encounter "load defaults" forms and the Leaflet map-marker editor popup. Called
// from modify_location.php, ft3d_scene_host.php, and the map-marker select popup.
//
// MODULE-SCOPE SAFE: the only eval-time work is installing one document-level 'input' listener (pure DOM,
// the markerColor "user changed" tracker) + the 7 window attaches. Every function body runs at
// form-interaction time, so its many deps (BASE_URL / BASE_URL_CDN / checkIfValidUUID (core-helpers),
// loader / LIGHT_TYPES / loadPostFunctions / ftDebugLog / queryDataInputs / applyEncounterAmbienceRow /
// icon_map (template.js), setTomSelectValue / ensureDynamicTomSelectOption (tom-select-init), openSideLog
// (side-panels-init), ICON_LOOKUP (inline)) resolve as window globals / shared global lexicals by call time
// → clean 2a fold. All 7 top-level functions are public (window-exported) — no module-private internals.
//
// PUBLIC GLOBALS (window-attached at the bottom): loadLocationDefaults, loadEncounterDefaults,
// getLocationsSelect, modifyLocationMarker, updateMarkerTypePreview, matchLocationMarkerType, modifyLocation.

import './world-gate-editor';
import { compileDefinitionInputs } from '../janus-engine/authoring/location-type-ambience';
import { locationDefaults } from '../janus-engine/authoring/location-defaults';
import { encounterDefaults } from '../janus-engine/authoring/encounter-defaults';
import { fromBaseRecord } from '../janus-engine/compile/from-base-context';

interface LocationPopupMarker {
    getLatLng(): unknown;
    bindPopup(content: string, options?: { maxWidth?: number; maxHeight?: number }): LocationPopupMarker;
    openPopup(): LocationPopupMarker;
    setPopupContent(content: string): void;
}

interface LocationMapApi {
    finishMarkerPlacement(marker: unknown, rosterMeta: unknown, encounterLinks: unknown): boolean;
}

let locationDefaultsSequence = 0;
let locationDefaultsRequestedKey = '';
const locationDefaultsKey = () => ['location_type', 'location_parent_id'].map(id => (document.getElementById(id) as HTMLInputElement | null)?.value ?? '').join('|');
let locationDefaultsPending: Promise<void> | null = null;
let locationDefaultsFailed = false;
function loadLocationDefaults(): Promise<void> {
    const sequence = ++locationDefaultsSequence;
    locationDefaultsRequestedKey = locationDefaultsKey();
    const value = (id: string) => (document.getElementById(id) as HTMLInputElement | null)?.value ?? '';
    const location_type = value('location_type'), location_parent_id = value('location_parent_id');
    const current = () => sequence === locationDefaultsSequence && value('location_type') === location_type
        && value('location_parent_id') === location_parent_id;
    locationDefaultsFailed = false;
    const task = (async () => {
        try {
            const response = await fetch(`${BASE_URL}options/tools/locations/load_location_defaults_inc.php`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ location_parent_id, location_type }),
            });
            if (!response.ok) throw new Error('Location inputs unavailable. Please retry loading defaults.');
            const raw: unknown = await response.json();
            if (!current()) return;
            if (!fromBaseRecord(raw) || raw.location_type !== location_type || raw.location_parent_id !== location_parent_id
                || (location_parent_id !== '' && !fromBaseRecord(raw.parent))) throw new Error('Location inputs incomplete. Please retry loading defaults.');
            const definitions = await compileDefinitionInputs(raw, location_type ? [location_type] : []);
            const type = definitions.get(location_type.toUpperCase()) ?? null;
            if (location_type && !type) throw new Error('Location type unavailable. Please retry loading defaults.');
            const defaults = locationDefaults(type, fromBaseRecord(raw.parent) ? raw.parent : null);
            const controls = [{ control: 'location_environment', id: defaults.environment }, ...defaults.ambience,
                ...(value('location_weather') === '' ? [{ control: 'location_weather', id: defaults.weather }] : [])];
            const preloadOptions = { silent: true, addOnly: true };
            await Promise.all(controls.map(row => ensureDynamicTomSelectOption(row.control, row.id, preloadOptions)));
            if (!current()) return;
            for (const row of controls) {
                const element = document.getElementById(row.control) as (HTMLSelectElement & { tomselect?: { options: Record<string, unknown> } }) | null;
                if (row.id && element?.tomselect && !Object.keys(element.tomselect.options).some(id => id.toUpperCase() === row.id.toUpperCase()))
                    throw new Error('Location options unavailable. Please retry loading defaults.');
            }
            for (const row of controls) if (row.control !== 'location_weather' || value(row.control) === '')
                setTomSelectValue(row.control, row.id, { silent: true });
            const inside = document.getElementById('location_inside') as HTMLInputElement | null;
            const population = document.getElementById('location_population') as HTMLInputElement | null;
            if (inside) inside.checked = defaults.inside;
            if (population) population.value = String(defaults.population);
        } catch (error) {
            if (!current()) return;
            locationDefaultsFailed = true;
            displayWarning(error instanceof Error ? error.message : 'Location inputs unavailable. Please retry.');
        }
    })();
    locationDefaultsPending = task;
    void task.finally(() => { if (locationDefaultsPending === task) locationDefaultsPending = null; });
    return task;
}

function wireLocationPageChanges(): void {
    const changed = (event: Event) => {
        const target = event.target as Element | null;
        const select = target?.closest<HTMLSelectElement>('[data-ft-location-change="location-type-defaults"]');
        if (!select) return;
        openItemInfoValue(select.id || 'location_type');
        if (locationDefaultsRequestedKey !== locationDefaultsKey() || locationDefaultsFailed) void loadLocationDefaults();
    };
    // New-location autosave is a target listener: install pending defaults before it serializes or redirects.
    document.addEventListener('input', changed, true);
    document.addEventListener('change', changed, true);
}

let encounterDefaultsSequence = 0;
let encounterDefaultsApplyQueue: Promise<void> = Promise.resolve();
async function loadEncounterDefaults(): Promise<void> {
    const sequence = ++encounterDefaultsSequence;
    const masterEl = document.getElementById('encounter_master_parent_id') as HTMLInputElement | null;
    const locEl = document.getElementById('encounter_location_id') as HTMLInputElement | null;
    if (!masterEl || !locEl) return;
    const encounter_master_parent_id = masterEl.value, encounter_location_id = locEl.value;
    const current = () => sequence === encounterDefaultsSequence && document.getElementById('encounter_location_id') === locEl
        && masterEl.value === encounter_master_parent_id && locEl.value === encounter_location_id;
    if (!checkIfValidUUID(encounter_master_parent_id) || !checkIfValidUUID(encounter_location_id)) return;
    const base = typeof BASE_URL !== 'undefined' ? BASE_URL : '/';
    try {
        const response = await fetch(`${base}options/tools/encounters/load_encounter_defaults.php`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ encounter_master_parent_id, encounter_location_id }),
        });
        if (!response.ok) throw new Error('Encounter defaults unavailable. Please retry.');
        const raw: unknown = await response.json();
        if (!current()) return;
        const defaults = await encounterDefaults(raw, encounter_master_parent_id, encounter_location_id);
        if (!current()) return;
        const controls = [{ control: 'encounter_environment', id: defaults.environment },
            { control: 'encounter_tempurature', id: defaults.temperature ?? '' }, { control: 'encounter_wind', id: defaults.wind ?? '' },
            ...(String((document.getElementById('encounter_weather') as HTMLInputElement | null)?.value ?? '') === ''
                ? [{ control: 'encounter_weather', id: defaults.weather }] : [])];
        const preload = { silent: true, addOnly: true, base };
        await Promise.all(controls.map(row => ensureDynamicTomSelectOption(row.control, row.id, preload)));
        const task = encounterDefaultsApplyQueue.catch(() => undefined).then(async () => {
            if (!current()) return;
            for (const row of controls) if (row.control !== 'encounter_weather'
                || String((document.getElementById(row.control) as HTMLInputElement | null)?.value ?? '') === '')
                setTomSelectValue(row.control, row.id, { silent: true });
            const zone = document.getElementById('encounter_zone_number') as HTMLInputElement | null;
            const inside = document.getElementById('encounter_inside') as HTMLInputElement | null;
            const population = document.getElementById('encounter_population') as HTMLInputElement | null;
            if (zone) zone.value = String(defaults.zone ?? '');
            if (inside) inside.checked = defaults.inside;
            if (population) population.value = String(defaults.population);
            const applyRow = applyEncounterAmbienceRow as (prefix: string, id: unknown, cat: unknown, sub: unknown, base: string, current: () => boolean) => Promise<void>;
            for (const slot of defaults.ambience) {
                await applyRow(slot.prefix, slot.id, slot.cat, slot.sub, base, current);
                if (!current()) return;
            }
            await loadPostFunctions();
        });
        encounterDefaultsApplyQueue = task;
        await task;
    } catch (error) {
        if (current()) displayWarning(error instanceof Error ? error.message : 'Encounter defaults unavailable. Please retry.');
    }
}

// Function to fetch locations and populate the marker popup
async function getLocationsSelect(marker: LocationPopupMarker): Promise<void> {

    const this_location_id = (document.getElementById('input_hidden_location_id') as HTMLInputElement).value;
    const location_master_parent_id = (document.getElementById('input_hidden_location_master_parent_id') as HTMLInputElement).value;
    const location_map_marker_coordinates = marker.getLatLng();


    // Use the existing loader from template.js
    marker.bindPopup(loader, { maxWidth: 300, maxHeight: 400 }).openPopup();

    try {
        const response = await fetch(`${BASE_URL}options/tools/location_map_markers/get_map_marker_select_inc.php`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                this_location_id,
                location_master_parent_id,
                location_map_marker_coordinates,
            })
        });

        const result = await response.text();

        // Populate the popup with the fetched content
        marker.setPopupContent(`<div class="popup-content">${result}</div>`);

        // Add event listeners if needed
        await loadPostFunctions();


    } catch (error) {
        console.error("Error in getLocationsSelect:", error);

        // Set an error message in the popup
        marker.setPopupContent('<div class="text-danger">Error loading locations.</div>');
    }
}

async function modifyLocationMarker(): Promise<void> {
    // Safely get the select elements
    const locationSelect  = document.getElementById('select_marker_location') as HTMLSelectElement | null;
    const encounterSelect = document.getElementById('select_marker_encounter') as HTMLSelectElement | null;

    // Get required data (with safeguards for potentially missing selects)
    const location_parent_id = (document.getElementById('input_hidden_location_id') as HTMLInputElement).value;
    const pic_id = (document.getElementById('input_hidden_location_map_pic_id') as HTMLInputElement).value;

    // Safely extract values – default to empty string if element missing or no selection
    const location_id  = locationSelect  ? (locationSelect.value?.trim()  || '') : '';
    const encounter_id = encounterSelect ? (encounterSelect.value?.trim() || '') : '';

    const location_map_marker_id = (document.getElementById('input_hidden_location_map_marker_id') as HTMLInputElement | null)?.value
        ? (document.getElementById('input_hidden_location_map_marker_id') as HTMLInputElement).value
        : null;
    const location_map_marker_type = (document.getElementById('markerType') as HTMLSelectElement).value;
    const location_map_marker_color = (document.getElementById('markerColor') as HTMLInputElement).value;
    const location_map_marker_coordinates_input = (document.getElementById('input_hidden_location_map_marker_coordinates') as HTMLInputElement).value;

    let location_map_marker_coordinates: unknown;
    try {
        const decodedCoordinates = location_map_marker_coordinates_input.replace(/&quot;/g, '"');
        location_map_marker_coordinates = JSON.parse(decodedCoordinates);
    } catch (error) {
        alert('Invalid marker coordinates. Please reselect the marker.');
        return;
    }

    // Ensure coordinates are available
    if (!location_map_marker_coordinates) {
        alert('Please place a marker on the map before saving.');
        return;
    }

    try {
        const response = await fetch(`${BASE_URL}options/tools/location_map_markers/modify_location_map_marker_inc.php`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                location_parent_id,
                pic_id,
                location_id,
                encounter_id,
                location_map_marker_id,
                location_map_marker_type,
                location_map_marker_color,
                location_map_marker_coordinates,
            }),
        });

        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const result = await response.json();
        if (result.status === 'success') {
            // Render the saved pin (and any encounter-link lines it completes) in
            // place — no full page reload. intMap exposes window.ftLocationMapApi
            // once the map is up; the endpoint returns the marker + links in the
            // same shape intMap consumes. Fall back to a reload only if the bridge
            // or marker payload is unavailable.
            const api = window.ftLocationMapApi;
            const rendered = api && result.marker
                ? api.finishMarkerPlacement(result.marker, result.rosterMeta, result.encounterLinks)
                : false;
            if (!rendered) {
                location.reload();
            }
        } else {
            alert(result.message || 'An error occurred.');
        }
    } catch (error) {
        console.error('Error in modifyLocationMarker:', error);
        alert('Unexpected error. Please try again.');
    }
}

// When the select is changed → update marker type, default color, AND clear the opposite select if needed
// Updates the live icon preview based on the current #markerType value
function updateMarkerTypePreview(): void {
    const select = document.getElementById('markerType') as HTMLSelectElement | null;
    const previewDiv = document.getElementById('markerTypePreview');
    if (!select || !previewDiv) return;

    const value = select.value;
    const text = select.options[select.selectedIndex]?.text || 'No type selected';

    if (value && ICON_LOOKUP && ICON_LOOKUP[value]) {
        const entry = ICON_LOOKUP[value];
        const iconFile = entry.file || 'help-circle-outline';
        const iconUrl = `${BASE_URL_CDN}images/icons/${iconFile}.png`;
        previewDiv.innerHTML = `<img src="${iconUrl}" width="32" height="32" alt="${text}"> <span class="ms-2">${text}</span>`;
    } else if (value) {
        // Fallback when no custom entry in ICON_LOOKUP
        previewDiv.innerHTML = `${icon_map(value, 32, 32, true)} <span class="ms-2">${text}</span>`;
    } else {
        previewDiv.innerHTML = `${icon_map('', 32, 32, true)} <span class="ms-2 text-secondary">No type selected</span>`;
    }

    syncMarkerColorToType(select);
}

// Sync the color picker to the category color of the chosen marker type — the
// single source of color truth for the popup, mirroring the PHP resolver
// (ft_resolve_map_marker_color). Each #markerType <option> is stamped with a
// PHP-computed data-marker-color carrying its LOCATION category (settlement gold /
// wilderness green / building tan / dungeon-danger crimson). Encounter markers
// read blue regardless of the type item. Never overrides a hand-picked color.
function syncMarkerColorToType(select: HTMLSelectElement): void {
    const colorInput = document.getElementById('markerColor') as HTMLInputElement | null;
    if (!colorInput || colorInput.dataset.userChanged) return;

    const encounterSelect = document.getElementById('select_marker_encounter') as HTMLSelectElement | null;
    const encounterId = encounterSelect?.value?.trim() || '';
    const isEncounter = !!encounterId && checkIfValidUUID(encounterId);

    const color = isEncounter
        ? '#007bff' // encounter = blue, always
        : (select.options[select.selectedIndex]?.dataset.markerColor || '');

    if (color) {
        colorInput.value = color;
        colorInput.style.backgroundColor = color;
    }
}

// Main function called when location or encounter select changes
async function matchLocationMarkerType(): Promise<void> {
    const locationSelect  = document.getElementById('select_marker_location') as HTMLSelectElement | null;
    const encounterSelect = document.getElementById('select_marker_encounter') as HTMLSelectElement | null;
    const markerTypeSelect = document.getElementById('markerType') as HTMLSelectElement | null;

    const location_id  = locationSelect?.value?.trim() || '';
    const encounter_id = encounterSelect?.value?.trim() || '';

    const isValidEncounter = encounter_id && checkIfValidUUID(encounter_id);
    const isValidLocation  = location_id && checkIfValidUUID(location_id);

    // Clear the opposite select — a marker attaches to a location XOR an encounter.
    // Color is derived downstream by updateMarkerTypePreview -> syncMarkerColorToType
    // (category color from the chosen type; encounter overrides to blue).
    if (isValidEncounter) {
        if (locationSelect) locationSelect.value = '';
    } else if (isValidLocation) {
        if (encounterSelect) encounterSelect.value = '';
    }

    // Fetch default marker type from server
    let defaultMarkerType = '';
    try {
        const response = await fetch(`${BASE_URL}options/tools/location_map_markers/match_location_marker_type_inc.php`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ location_id, encounter_id }),
        });

        if (response.ok) {
            const result = await response.json();
            defaultMarkerType = result.markerType_value || '';
        }
    } catch (e) {
        console.error('Failed to fetch default marker type:', e);
    }

    // Set the plain select
    if (markerTypeSelect) {
        markerTypeSelect.value = defaultMarkerType;
    }

    // Update live preview (also syncs the color picker to the type's category color).
    updateMarkerTypePreview();
}

// Mark color picker as manually changed when user interacts
document.addEventListener('input', function (e) {
    const t = e.target as HTMLElement | null;
    if (t && t.id === 'markerColor') {
        t.dataset.userChanged = 'true';
    }
});

async function modifyLocation(): Promise<boolean> {
    while (locationDefaultsPending) await locationDefaultsPending;
    if (locationDefaultsFailed) {
        const message = 'Retry loading location defaults before saving.';
        displayWarning(message); throw new Error(message);
    }

    openSideLog();

    const result: unknown = await queryDataInputs(
        BASE_URL + "options/tools/locations/modify_location_inc.php",
        'div_sidelog_warning',
        {
            location_master_parent_id: 'location_master_parent_id',
            location_id: 'hidden_input_modify_location_id',
            location_name: 'location_name',
            location_parent_id: 'location_parent_id',
            location_type: 'location_type',
            location_world: 'location_world',
            location_zone_prefix: 'location_zone_prefix',
            location_description: 'location_description',
            location_gm_notes: 'location_gm_notes',
            location_private: 'location_private',
            location_ecosystem: 'location_ecosystem',
            location_ecosystem_cycles: 'location_ecosystem_cycles',
            location_localized_phenomena: 'location_localized_phenomena',
            location_flora_and_fauna: 'location_flora_and_fauna',
            location_founding_date_year: 'location_founding_date_year',
            location_founding_date_month: 'location_founding_date_month',
            location_founding_date_day: 'location_founding_date_day',
            location_founding_date_era: 'location_founding_date_era',
            location_population: 'location_population',
            location_inhabitants_demonym: 'location_inhabitants_demonym',
            location_demographics: 'location_demographics',
            location_government: 'location_government',
            location_infrastructure: 'location_infrastructure',
            location_districts: 'location_districts',
            location_assets: 'location_assets',
            location_defenses: 'location_defenses',
            location_industry_and_trade: 'location_industry_and_trade',
            location_guilds_and_factions: 'location_guilds_and_factions',
            location_points_of_interest: 'location_points_of_interest',
            location_geography: 'location_geography',
            location_climate: 'location_climate',
            location_natural_resources: 'location_natural_resources',
            location_construction_date_year: 'location_construction_date_year',
            location_construction_date_month: 'location_construction_date_month',
            location_construction_date_day: 'location_construction_date_day',
            location_construction_date_era: 'location_construction_date_era',
            location_date_that_it_became_a_ruin_year: 'location_date_that_it_became_a_ruin_year',
            location_date_that_it_became_a_ruin_month: 'location_date_that_it_became_a_ruin_month',
            location_date_that_it_became_a_ruin_day: 'location_date_that_it_became_a_ruin_day',
            location_date_that_it_became_a_ruin_era: 'location_date_that_it_became_a_ruin_era',
            location_alternative_names: 'location_alternative_names',
            location_purpose: 'location_purpose',
            location_alterations: 'location_alterations',
            location_architecture: 'location_architecture',
            location_history: 'location_history',
            location_tourism: 'location_tourism',
        },
        true, // show loader
        true, // runPostFunctions
        false // unfocus_inputs

    );

    const savedId = typeof result === 'string' && result !== ''
        ? new DOMParser().parseFromString(result, 'text/html')
            .querySelector('[data-ft-location-saved-id]')?.getAttribute('data-ft-location-saved-id')
        : null;
    const saved = Boolean(savedId) && savedId === String(getElementValue('hidden_input_modify_location_id') ?? '');
    if (!saved && !result) displayWarning('Location was not saved. Please retry.');
    return saved;
}

function wireLocationToolActions(): void {
    document.addEventListener('click', (event) => {
        const target = event.target as HTMLElement | null;
        const button = target?.closest<HTMLElement>('[data-ft-location-action="derive-encounter"]');
        if (!button) return;
        if (typeof queryData !== 'function') return;
        queryData(
            button.dataset.ftEndpoint || '',
            button.dataset.ftTargetId || '',
            {
                location_id: button.dataset.ftLocationId || '',
                encounter_master_parent_id: button.dataset.ftEncounterMasterParentId || '',
            },
        );
    });
}


// ===========================================================================================
// Window attachment — preserve the public surface (7 globals, all PHP-onclick / map-marker callers).
// ===========================================================================================
declare global {
    interface Window {
        loadLocationDefaults: typeof loadLocationDefaults;
        loadEncounterDefaults: typeof loadEncounterDefaults;
        getLocationsSelect: typeof getLocationsSelect;
        modifyLocationMarker: typeof modifyLocationMarker;
        updateMarkerTypePreview: typeof updateMarkerTypePreview;
        matchLocationMarkerType: typeof matchLocationMarkerType;
        modifyLocation: typeof modifyLocation;
        ftLocationMapApi?: LocationMapApi;
    }
}

window.loadLocationDefaults = loadLocationDefaults;
window.loadEncounterDefaults = loadEncounterDefaults;
window.getLocationsSelect = getLocationsSelect;
window.modifyLocationMarker = modifyLocationMarker;
window.updateMarkerTypePreview = updateMarkerTypePreview;
window.matchLocationMarkerType = matchLocationMarkerType;
window.modifyLocation = modifyLocation;

wireLocationToolActions();
wireLocationPageChanges();

export {};
